import { RouterProvider } from "@tanstack/react-router";
import { type ReactNode, StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { ConnectionTest } from "../../auth/web/ConnectionTest.tsx";
import { externalSession } from "../../external/web/session.ts";
import { RequestError } from "./client.ts";
import { LoadErrorCard } from "./GateCards.tsx";
import { portalEntry } from "./portal-entry.ts";
import { RootInfo } from "./RootInfo.tsx";
import { RouteContent } from "./RouteContent.tsx";
import { createAppRouter, type PortalTreeKind } from "./router.tsx";
import { classifyPath, legacyRedirectTarget, treeKindForProbe } from "./startup.ts";
import { ThemeProvider } from "./theme.ts";
import "./ui/theme.css";

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("missing #root");
const root = createRoot(rootEl);

const show = (page: ReactNode): void =>
  root.render(
    <StrictMode>
      <ThemeProvider>{page}</ThemeProvider>
    </StrictMode>,
  );

const sessionKind = (): Promise<PortalTreeKind | null> =>
  externalSession().then(
    (session) => treeKindForProbe(session),
    (error: unknown) => treeKindForProbe({ status: error instanceof RequestError ? error.status : null }),
  );

// The server also redirects legacy paths; this covers the Vite dev server, which serves the SPA itself.
const redirectLegacy = async (): Promise<void> => {
  const { internalSlug } = await portalEntry();
  const target = legacyRedirectTarget(window.location, internalSlug);
  if (target) window.location.replace(target);
};

const startTenant = async (slug: string): Promise<void> => {
  const kind = await sessionKind();
  if (kind === null) {
    return show(
      <div className="app-main">
        <LoadErrorCard onRetry={() => void startTenant(slug)} />
      </div>,
    );
  }
  return show(<RouterProvider router={createAppRouter(slug, kind)} />);
};

const start = async (): Promise<void> => {
  const route = classifyPath(window.location.pathname);
  switch (route.kind) {
    case "legacy":
      return redirectLegacy().catch(() => show(<RootInfo />));
    case "connection-test":
      return show(
        <div className="app-main">
          <a className="skip-link" href="#main-content">
            Skip to content
          </a>
          <RouteContent pathname={window.location.pathname}>
            <ConnectionTest />
          </RouteContent>
        </div>,
      );
    case "tenant":
      return startTenant(route.slug);
    default:
      return show(<RootInfo />);
  }
};

void start();
